import React, { ChangeEvent, FC, ReactNode, useState, useEffect } from 'react';
import { EditDealContext } from './context';
import { DealMetaDataType, DealType, EditDealType } from 'models/deals.model';
import { apiService } from 'services/base.service';
import { convertTimeString, handleApiError } from 'utils/functions';
import { BrandType } from 'models/brand.model';
import { UserType } from 'models/user.model';
import { useParams } from 'react-router-dom';
import { MealType } from 'models/meal.model';

const EditDealProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [editDealForm, setEditDealForm] = useState({} as EditDealType);
  const [initialEditData, setInitialEditData] = useState({} as EditDealType);
  const [disabled, setDisabled] = useState(false);
  const [dealTypesMetaData, setDealTypesMetaData] = useState({
    dealTypes: [] as DealMetaDataType[],
    dealRequirementTypes: [] as DealMetaDataType[],
    dealValueTypes: [] as DealMetaDataType[],
    dealEligibilityTypes: [] as DealMetaDataType[]
  });

  const [brands, setBrands] = useState<BrandType[]>([]);
  const [meals, setMeals] = useState<MealType[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [userSearchValue, setUserSearchValue] = useState('');
  const [dealToEdit, setDealToEdit] = useState<DealType | null>(null);
  const [dealLoading, setDealLoading] = useState(false);
  const { dealId } = useParams();

  const fetchDealToEdit = async () => {
    try {
      setDealLoading(true);
      const res = await apiService.get(`marketing/deal/${dealId}`);
      setDealToEdit(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setDealLoading(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setEditDealForm((prevFormData) => {
      return {
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value
      };
    });
  };

  const fetchDealTypes = async () => {
    try {
      const res = await apiService.get('internal/deal-types', {
        isInternal: true
      });
      const {
        deal_types: dealTypes,
        deal_requirement_types: dealRequirementTypes,
        deal_value_types: dealValueTypes,
        deal_eligibility_types: dealEligibilityTypes
      } = res.data.data;
      setDealTypesMetaData({
        dealTypes,
        dealValueTypes,
        dealEligibilityTypes,
        dealRequirementTypes
      });
    } catch (error) {
      setDealTypesMetaData({
        dealTypes: [],
        dealRequirementTypes: [],
        dealValueTypes: [],
        dealEligibilityTypes: []
      });
      handleApiError(error);
    }
  };

  const fetchBrands = async () => {
    if (!editDealForm.cokitchen_id) return;
    try {
      const res = await apiService.get('brand_management/brands', {
        isInternal: true,
        params: {
          live: true,
          cokitchen_id: editDealForm.cokitchen_id
        }
      });
      setBrands(res.data);
    } catch (error) {
      setBrands([]);
      handleApiError(error);
    }
  };

  const fetchMeals = async () => {
    if (!brands.length) return;
    try {
      const res = await apiService.get('meal/bulk/brands', {
        params: {
          brand_ids: brands.map((b) => b.id)
        }
      });
      setMeals(res.data.data);
    } catch (error) {
      setMeals([]);
      handleApiError(error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiService.get('marketing/user', {
        params: {
          search: userSearchValue
        }
      });
      setUsers(res.data.data);
    } catch (error) {
      setUsers([]);
      handleApiError(error);
    }
  };

  useEffect(() => {
    fetchDealTypes();
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [editDealForm.cokitchen_id]);

  useEffect(() => {
    fetchMeals();
  }, [brands]);

  useEffect(() => {
    fetchUsers();
  }, [userSearchValue]);

  useEffect(() => {
    fetchDealToEdit();
  }, [dealId]);

  useEffect(() => {
    if (dealToEdit) {
      const formattedDealData = {
        ...editDealForm,
        heading: dealToEdit.heading,
        title: dealToEdit.title,
        body: dealToEdit.body,
        images: dealToEdit.images,
        cokitchen_id: dealToEdit.cokitchen_id,
        private: dealToEdit.private,
        discount_code: dealToEdit.discount_code,
        deal_value_type_id: dealToEdit.deal_value_type_id,
        deal_type_id: dealToEdit.deal_type_id,
        cap: dealToEdit.cap ? String(dealToEdit.cap) : '',
        brands: dealToEdit.brands || [],
        meal_ids: dealToEdit.meal_ids || [],
        min_amount: dealToEdit.min_amount,
        min_items: dealToEdit.min_items,
        specific_customers: dealToEdit.specific_customers,
        notification: dealToEdit.notification || false,
        active: dealToEdit.active,
        rate: dealToEdit.rate,
        fixed_amount: dealToEdit.fixed_amount,
        max_usage: dealToEdit.max_usage,
        post: dealToEdit?.post || false,
        deal_requirement_type_id: dealToEdit.deal_requirement_type_id,
        deal_eligibility_type_id: dealToEdit.deal_eligibility_type_id,
        to_start_date: new Date(dealToEdit.to_start_date),
        to_expire_date: dealToEdit.to_expire_date ? new Date(dealToEdit.to_expire_date) : undefined,
        to_start_time: convertTimeString(dealToEdit.to_start_time),
        to_expire_time: dealToEdit.to_expire_time
          ? convertTimeString(dealToEdit.to_expire_time)
          : null
      };
      setEditDealForm(formattedDealData);
      setInitialEditData(formattedDealData);
    }
  }, [dealToEdit]);

  return (
    <EditDealContext.Provider
      value={{
        editDealForm,
        setEditDealForm,
        disabled,
        setDisabled,
        handleChange,
        dealValueTypes: dealTypesMetaData.dealValueTypes,
        dealTypes: dealTypesMetaData.dealTypes,
        brands,
        dealRequirementTypes: dealTypesMetaData.dealRequirementTypes,
        dealEligibilityTypes: dealTypesMetaData.dealEligibilityTypes,
        users,
        setUserSearchValue,
        initialEditData,
        dealToEdit,
        setDealToEdit,
        dealLoading,
        meals
      }}>
      {children}
    </EditDealContext.Provider>
  );
};

export default EditDealProvider;
